import { useEffect, useState } from "react";
import api, { formatApiError, eur } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Loader2, TrendingUp, Lightbulb, ListChecks, BrainCircuit, UserRound, CheckCircle2, AlertTriangle, Mail, Send } from "lucide-react";

const PERIODS = [
  ["giorno", "Giorno"], ["settimana", "Settimana"], ["mese", "Mese"], ["bimestre", "Bimestre"],
  ["trimestre", "Trimestre"], ["quadrimestre", "Quadrimestre"], ["semestre", "Semestre"], ["anno", "Anno"],
  ["anno_corrente", "Anno in corso"],
];
const SUBJECTS = [
  ["rete", "Tutta la rete"], ["agente", "Singolo agente/agenzia"],
  ["responsabile", "Responsabile Commerciale"], ["hop", "Head of Partner"],
];

const buildReportText = (a, data) => {
  const L = [];
  L.push(a.titolo || "Report Direzionale");
  const hdr = [data?.soggetto, data?.periodo].filter(Boolean).join(" · ");
  if (hdr) L.push(hdr);
  L.push("");
  if (a.sintesi) { L.push(a.sintesi); L.push(""); }
  if (Array.isArray(a.punti_chiave) && a.punti_chiave.length) { L.push("PUNTI CHIAVE:"); a.punti_chiave.forEach((p) => L.push(`- ${p}`)); L.push(""); }
  (a.tabelle || []).forEach((t) => {
    L.push(`${(t.titolo || "Tabella").toUpperCase()}:`);
    if (Array.isArray(t.colonne) && t.colonne.length) L.push(t.colonne.join(" | "));
    (t.righe || []).forEach((r) => L.push((r || []).map(String).join(" | ")));
    L.push("");
  });
  if (Array.isArray(a.raccomandazioni) && a.raccomandazioni.length) { L.push("RACCOMANDAZIONI:"); a.raccomandazioni.forEach((p, i) => L.push(`${i + 1}. ${p}`)); }
  return L.join("\n").trim();
};

export default function MicheleTorri() {
  const [period, setPeriod] = useState("trimestre");
  const [subjectType, setSubjectType] = useState("rete");
  const [subjectId, setSubjectId] = useState("");
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [respLoading, setRespLoading] = useState(false);
  const [resp, setResp] = useState(null);
  const [mailTo, setMailTo] = useState("");
  const [mailSubject, setMailSubject] = useState("");
  const [mailBody, setMailBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/agencies").then((r) => setAgencies(r.data || [])).catch(() => {}); }, []);

  useEffect(() => {
    if (result?.analysis) {
      setMailSubject(result.analysis.titolo || "Report Direzionale Torri");
      setMailBody(buildReportText(result.analysis, result.data));
    }
  }, [result]);

  const sendReport = async () => {
    const list = mailTo.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) { toast.error("Inserisci almeno un destinatario"); return; }
    if (!mailBody.trim()) { toast.error("Il testo del report è vuoto"); return; }
    setSending(true);
    try {
      const r = await api.post("/ai-analysis/michele/send", { subject: mailSubject, recipients: list, body_text: mailBody });
      toast.success(r.data?.message || "Report inviato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio fallito"); }
    finally { setSending(false); }
  };

  const run = async () => {
    if (subjectType === "agente" && !subjectId) { toast.error("Seleziona un agente/agenzia"); return; }
    setLoading(true); setResult(null);
    try {
      const r = await api.post("/ai-analysis/michele", {
        period, subject_type: subjectType,
        subject_id: subjectType === "agente" ? subjectId : null,
      }, { timeout: 180000 });
      setResult(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore durante l'analisi"); }
    finally { setLoading(false); }
  };

  const evalResp = async () => {
    setRespLoading(true); setResp(null);
    try {
      const r = await api.post("/ai-analysis/responsabile-eval", { period }, { timeout: 180000 });
      setResp(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore durante la valutazione"); }
    finally { setRespLoading(false); }
  };

  const a = result?.analysis;
  const rk = resp?.kpis;
  const rv = resp?.verdict;
  const metrics = rk ? [
    { label: "Affiancamenti", val: `${rk.affiancamenti_svolti}/${rk.affiancamenti_previsti}`, pct: rk.affiancamenti_pct, sotto: rk.affiancamenti_sotto },
    { label: "Partner convertiti", val: `${rk.conversioni_svolte}/${rk.conversioni_previste}`, pct: rk.conversioni_pct, sotto: rk.conversioni_sotto },
    { label: "Compliance rete", val: rk.compliance_rete != null ? `${rk.compliance_rete}%` : "N/D", pct: rk.compliance_rete, target: rk.compliance_target, sotto: rk.compliance_sotto },
    { label: "Azioni su clienti", val: rk.copertura_azioni != null ? `${rk.copertura_azioni}%` : "N/D", pct: rk.copertura_azioni, target: rk.copertura_azioni_target, sotto: rk.copertura_azioni_sotto },
    { label: "Copertura vendite", val: rk.copertura_fatturato != null ? `${rk.copertura_fatturato}%` : "N/D", pct: rk.copertura_fatturato, target: rk.copertura_fatturato_target, sotto: rk.copertura_fatturato_sotto },
  ] : [];

  return (
    <div className="space-y-6" data-testid="michele-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-3xl font-black tracking-tight">
            <BrainCircuit className="h-7 w-7 text-primary" /> Dashboard Michele Torri
          </h1>
          <p className="text-sm text-muted-foreground">Analisi direzionale generata da AI su periodo e soggetto selezionati.</p>
        </div>
      </div>

      <Card className="p-5" data-testid="michele-filters">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Periodo (relativo a oggi)</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="michele-period"><SelectValue /></SelectTrigger>
              <SelectContent>{PERIODS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Soggetto</Label>
            <Select value={subjectType} onValueChange={(v) => { setSubjectType(v); setSubjectId(""); }}>
              <SelectTrigger data-testid="michele-subject"><SelectValue /></SelectTrigger>
              <SelectContent>{SUBJECTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {subjectType === "agente" && (
            <div className="space-y-1.5">
              <Label className="text-xs">Agente / Agenzia</Label>
              <Select value={subjectId} onValueChange={setSubjectId}>
                <SelectTrigger data-testid="michele-agency"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{agencies.map((ag) => <SelectItem key={ag.id} value={ag.id}>{ag.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-end">
            <Button onClick={run} disabled={loading} className="w-full" data-testid="michele-run-btn">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Analisi in corso…</> : <><Sparkles className="mr-2 h-4 w-4" /> Genera analisi AI</>}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-5" data-testid="resp-eval-card">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <UserRound className="h-5 w-5 text-primary" />
            <h2 className="font-heading text-lg font-bold tracking-tight">Valutazione Responsabile Commerciale</h2>
          </div>
          <Button onClick={evalResp} disabled={respLoading} variant="outline" data-testid="resp-eval-btn">
            {respLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Valutazione…</> : <><Sparkles className="mr-2 h-4 w-4" /> Valuta ({PERIODS.find(([v]) => v === period)?.[1]})</>}
          </Button>
        </div>

        {!resp && !respLoading && (
          <p className="text-sm text-muted-foreground" data-testid="resp-eval-empty">Genera la valutazione del Responsabile Commerciale (KPI + giudizio AI) sul periodo selezionato in alto.</p>
        )}
        {respLoading && (
          <div className="flex items-center justify-center gap-3 py-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-primary" /> L'AI sta valutando la performance…</div>
        )}

        {resp && !respLoading && (
          <div className="space-y-5" data-testid="resp-eval-result">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {metrics.map((m) => (
                <div key={m.label} className={`rounded-lg border p-3 ${m.sotto ? "border-red-500/40 bg-red-500/10" : "border-green-500/30 bg-green-500/10"}`} data-testid={`resp-kpi-${m.label}`}>
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{m.label}</div>
                  <div className="mt-1 font-heading text-xl font-black">{m.val}</div>
                  <div className="mt-1 flex items-center gap-1 text-[11px]">
                    {m.sotto ? <AlertTriangle className="h-3 w-3 text-red-400" /> : <CheckCircle2 className="h-3 w-3 text-green-400" />}
                    <span className={m.sotto ? "text-red-300" : "text-green-300"}>{m.sotto ? "Sotto target" : "In linea"}</span>
                    {m.target != null && <span className="text-muted-foreground">· obiettivo {m.target}%</span>}
                    {m.pct != null && m.target == null && <span className="text-muted-foreground">· {m.pct}%</span>}
                  </div>
                </div>
              ))}
            </div>
            <div className="text-xs text-muted-foreground">Vendite rete anno: <span className="font-mono text-foreground">{eur(rk.fatturato_rete)}</span> / obiettivo <span className="font-mono text-foreground">{eur(rk.target_rete)}</span></div>

            {rv && (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-4" data-testid="resp-verdict">
                <div className="flex items-center gap-3">
                  {rv.voto != null && (
                    <div className={`flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-full border-2 ${rv.voto >= 70 ? "border-green-500 text-green-400" : rv.voto >= 50 ? "border-amber-500 text-amber-400" : "border-red-500 text-red-400"}`}>
                      <span className="font-heading text-lg font-black leading-none">{rv.voto}</span>
                      <span className="text-[8px] uppercase text-muted-foreground">voto</span>
                    </div>
                  )}
                  <p className="text-sm leading-relaxed text-foreground/90">{rv.giudizio}</p>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                  {Array.isArray(rv.punti_forza) && rv.punti_forza.length > 0 && (
                    <div>
                      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-green-400"><CheckCircle2 className="h-3.5 w-3.5" /> Punti di forza</div>
                      <ul className="space-y-1">{rv.punti_forza.map((p, i) => <li key={i} className="flex gap-2 text-sm"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-green-500" />{p}</li>)}</ul>
                    </div>
                  )}
                  {Array.isArray(rv.aree_miglioramento) && rv.aree_miglioramento.length > 0 && (
                    <div>
                      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-amber-400"><AlertTriangle className="h-3.5 w-3.5" /> Aree di miglioramento</div>
                      <ul className="space-y-1">{rv.aree_miglioramento.map((p, i) => <li key={i} className="flex gap-2 text-sm"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />{p}</li>)}</ul>
                    </div>
                  )}
                </div>
                {Array.isArray(rv.raccomandazioni) && rv.raccomandazioni.length > 0 && (
                  <div className="mt-4">
                    <div className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-primary"><Lightbulb className="h-3.5 w-3.5" /> Raccomandazioni</div>
                    <ul className="space-y-1">{rv.raccomandazioni.map((p, i) => <li key={i} className="flex gap-2 text-sm"><span className="font-bold text-primary">{i + 1}.</span>{p}</li>)}</ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Card>

      {loading && (
        <Card className="flex items-center justify-center gap-3 p-12 text-muted-foreground" data-testid="michele-loading">
          <Loader2 className="h-5 w-5 animate-spin text-primary" /> L'AI sta analizzando i dati…
        </Card>
      )}

      {a && !loading && (
        <div className="space-y-6" data-testid="michele-result">
          <Card className="p-6">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <TrendingUp className="h-4 w-4 text-primary" /> {result?.data?.soggetto} · {result?.data?.periodo}
            </div>
            <h2 className="font-heading text-2xl font-black tracking-tight">{a.titolo}</h2>
            {a.sintesi && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-foreground/90">{a.sintesi}</p>}
          </Card>

          {Array.isArray(a.punti_chiave) && a.punti_chiave.length > 0 && (
            <Card className="p-6" data-testid="michele-keypoints">
              <div className="mb-3 flex items-center gap-2"><ListChecks className="h-4 w-4 text-primary" /><h3 className="font-heading text-lg font-bold tracking-tight">Punti chiave</h3></div>
              <ul className="space-y-2">
                {a.punti_chiave.map((p, i) => (
                  <li key={i} className="flex gap-2 text-sm"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" /><span>{p}</span></li>
                ))}
              </ul>
            </Card>
          )}

          {Array.isArray(a.tabelle) && a.tabelle.map((t, ti) => (
            <Card key={ti} className="p-6" data-testid={`michele-table-${ti}`}>
              <h3 className="mb-3 font-heading text-lg font-bold tracking-tight">{t.titolo}</h3>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>{(t.colonne || []).map((c, ci) => <TableHead key={ci}>{c}</TableHead>)}</TableRow>
                  </TableHeader>
                  <TableBody>
                    {(t.righe || []).map((row, ri) => (
                      <TableRow key={ri}>{(row || []).map((cell, ci) => <TableCell key={ci} className="text-sm">{String(cell)}</TableCell>)}</TableRow>
                    ))}
                    {(t.righe || []).length === 0 && <TableRow><TableCell colSpan={(t.colonne || []).length || 1} className="py-6 text-center text-muted-foreground">Nessun dato</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </Card>
          ))}

          {Array.isArray(a.raccomandazioni) && a.raccomandazioni.length > 0 && (
            <Card className="border-primary/30 bg-primary/5 p-6" data-testid="michele-reco">
              <div className="mb-3 flex items-center gap-2"><Lightbulb className="h-4 w-4 text-primary" /><h3 className="font-heading text-lg font-bold tracking-tight">Raccomandazioni</h3></div>
              <ul className="space-y-2">
                {a.raccomandazioni.map((p, i) => (
                  <li key={i} className="flex gap-2 text-sm"><span className="mt-0.5 font-bold text-primary">{i + 1}.</span><span>{p}</span></li>
                ))}
              </ul>
            </Card>
          )}

          <Card className="p-6" data-testid="michele-send-card">
            <div className="mb-3 flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /><h3 className="font-heading text-lg font-bold tracking-tight">Invia report via email</h3></div>
            <p className="mb-4 text-xs text-muted-foreground">Inserisci manualmente i destinatari e, se vuoi, modifica il testo del report prima dell'invio.</p>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Destinatari (separati da virgola, spazio o a capo)</Label>
                <Textarea value={mailTo} onChange={(e) => setMailTo(e.target.value)} rows={2} placeholder="mario@rossi.it, laura@bianchi.it" data-testid="michele-mail-to" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Oggetto</Label>
                <Input value={mailSubject} onChange={(e) => setMailSubject(e.target.value)} data-testid="michele-mail-subject" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Testo del report (modificabile prima dell'invio)</Label>
                <Textarea value={mailBody} onChange={(e) => setMailBody(e.target.value)} rows={14} className="font-mono text-xs leading-relaxed" data-testid="michele-mail-body" />
              </div>
              <div className="flex justify-end">
                <Button onClick={sendReport} disabled={sending} data-testid="michele-mail-send-btn">
                  {sending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Invio…</> : <><Send className="mr-2 h-4 w-4" /> Invia report</>}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {!a && !loading && (
        <Card className="p-12 text-center text-sm text-muted-foreground" data-testid="michele-empty">
          Seleziona periodo e soggetto, poi premi <span className="font-semibold text-foreground">Genera analisi AI</span> per ottenere il report.
        </Card>
      )}
    </div>
  );
}
